import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import EventScheduler from './components/EventScheduler';
import ErrorBoundary from './components/ErrorBoundary';
import { DiagnosticsPanel } from './components/DiagnosticsPanel';
import { diagnosticsConfig, toggleDiagnostics } from './config/diagnostics';
import { useAuth } from './hooks/useAuth';

// Fix: ensure Leaflet marker icons resolve correctly on mobile builds
import L from 'leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

// Optional: explicit icon instance (used for dropped pin)
const defaultMarkerIcon = new L.Icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  tooltipAnchor: [16, -28],
  shadowSize: [41, 41],
});

// Context for global state management
const AppContext = createContext();

// Translation data for multi-language support
const translations = {
  en: {
    appName: 'HopeLink',
    login: 'Login',
    logout: 'Logout',
    signup: 'Sign Up',
    home: 'Home',
    dashboard: 'Dashboard',
    profile: 'Profile',
    matches: 'Matches',
    donations: 'Donations',
    admin: 'Admin Panel',
    language: 'Language',
    welcome: 'Welcome to HopeLink, a community to support cancer patients.',
    findHelp: 'Find Help',
    offerHelp: 'Offer Help',
    patientNeeds: 'Patient Needs',
    volunteerOffers: 'Volunteer Offers',
    noMatches: 'No matches found yet. Check back later!',
    patientName: 'Patient Name',
    volunteerName: 'Volunteer Name',
    needs: 'Needs',
    offers: 'Offers',
    schedule: 'Schedule',
    viewProfile: 'View Profile',
    location: 'Location',
    rating: 'Rating',
    tip: 'Tip Volunteer',
    aboutMe: 'About Me',
    myNeeds: 'My Needs',
    myOffers: 'My Offers',
    saveProfile: 'Save Profile',
    addNeed: 'Add a Need',
    addOffer: 'Add an Offer',
    delete: 'Delete',
    venmo: 'Venmo ID',
    paypay: 'PayPay ID',
    donationPool: 'Donation Pool',
    donate: 'Donate',
    donationMessage: 'Your donation helps distribute funds to highly-rated volunteers.',
    adminControl: 'Admin Ad Management',
    adStatus: 'Ad Status',
    adRevenue: 'Total Ad Revenue',
    toggleAds: 'Toggle Ads',
    eulaAccept: 'I accept the Terms & Conditions and EULA.',
    submit: 'Submit',
    loginPrompt: 'Please log in to continue.',
    anonRating: 'Anonymous Rating',
    demenaor: 'Demeanor',
    friendly: 'Friendliness',
    excellent: 'Excellent',
    good: 'Good',
    average: 'Average',
    poor: 'Poor',
    locationMatch: 'Location Match',
    timeMatch: 'Time Match',
    matchNotification: 'You have a new match!',
    patient: 'Patient',
    volunteer: 'Volunteer',
    adminLabel: 'Admin',
    noUsersFound: 'No users found.',
    profileSaveSuccess: 'Profile updated successfully!',
    profileSaveError: 'Failed to update profile.',
    mission: 'HopeLink is dedicated to connecting cancer patients with compassionate volunteers who can assist with daily tasks, providing a network of support and care during a challenging time.',
    email: 'Email',
    password: 'Password',
    dontHaveAccount: "Don't have an account?",
    alreadyHaveAccount: 'Already have an account?',
    scheduler: {
      title: "Schedule a meeting",
      clearChanges: "Clear Changes",
      topic: "Topic",
      topicPlaceholder: "Lorem Ipsum Dolor Meeting",
      date: "Date",
      datePlaceholder: "23 May, Mon, 2022",
      start: "Start",
      startPlaceholder: "10:30 am",
      end: "End",
      endPlaceholder: "11:00 am",
      oneTime: "One-time",
      weekly: "Weekly",
      monthly: "Monthly",
      where: "Where",
      locationPlaceholder: "Meeting Room 1",
      description: "Description",
      descriptionPlaceholder: "Add description...",
      inviteMembers: "Invite Members",
      invitePlaceholder: "Add users or groups...",
      sendInvite: "SEND INVITE",
      create: "CREATE"
    },
    role: 'Role',
  },
  ja: {
    appName: 'ホープリンク',
    login: 'ログイン',
    logout: 'ログアウト',
    signup: 'サインアップ',
    home: 'ホーム',
    dashboard: 'ダッシュボード',
    profile: 'プロフィール',
    matches: 'マッチング',
    donations: '寄付',
    admin: '管理パネル',
    language: '言語',
    welcome: 'キャンサー患者を支援するコミュニティ、ホープリンクへようこそ。',
    findHelp: '助けを見つける',
    offerHelp: '助けを提供する',
    patientNeeds: '患者のニーズ',
    volunteerOffers: 'ボランティアの提供',
    noMatches: 'まだマッチングが見つかりません。後でまたチェックしてください！',
    patientName: '患者名',
    volunteerName: 'ボランティア名',
    needs: 'ニーズ',
    offers: '提供',
    schedule: 'スケジュール',
    viewProfile: 'プロフィールを見る',
    location: '場所',
    rating: '評価',
    tip: 'ボランティアにチップを送る',
    aboutMe: '自己紹介',
    myNeeds: '私のニーズ',
    myOffers: '私の提供',
    saveProfile: 'プロフィールを保存',
    addNeed: 'ニーズを追加',
    addOffer: '提供を追加',
    delete: '削除',
    venmo: 'Venmo ID',
    paypay: 'PayPay ID',
    donationPool: '寄付プール',
    donate: '寄付する',
    donationMessage: 'あなたの寄付は、評価の高いボランティアに資金を分配するのに役立ちます。',
    adminControl: '管理者広告管理',
    adStatus: '広告ステータス',
    adRevenue: '総広告収入',
    toggleAds: '広告を切り替える',
    eulaAccept: '利用規約とEULAに同意します。',
    submit: '送信',
    loginPrompt: '続けるにはログインしてください。',
    anonRating: '匿名評価',
    demenaor: '態度',
    friendly: '親しみやすさ',
    excellent: '素晴らしい',
    good: '良い',
    average: '普通',
    poor: '悪い',
    locationMatch: '場所の一致',
    timeMatch: '時間の一致',
    matchNotification: '新しいマッチングがあります！',
    patient: '患者',
    volunteer: 'ボランティア',
    adminLabel: '管理者',
    noUsersFound: 'ユーザーが見つかりません。',
    profileSaveSuccess: 'プロフィールが正常に更新されました！',
    profileSaveError: 'プロフィールの更新に失敗しました。',
    mission: 'ホープリンクは、日常のタスクを支援できる思いやりのあるボランティアとがん患者をつなぐことに専念しており、困難な時期にサポートとケアのネットワークを提供します。',
    email: 'メールアドレス',
    password: 'パスワード',
    dontHaveAccount: 'アカウントをお持ちではありませんか？',
    alreadyHaveAccount: 'すでにアカウントをお持ちですか？',
    scheduler: {
      title: "会議をスケジュール",
      clearChanges: "変更をクリア",
      topic: "トピック",
      topicPlaceholder: "Lorem Ipsum Dolor会議",
      date: "日付",
      datePlaceholder: "2022年5月23日(月)",
      start: "開始",
      startPlaceholder: "午前10:30",
      end: "終了",
      endPlaceholder: "午前11:00",
      oneTime: "一回限り",
      weekly: "毎週",
      monthly: "毎月",
      where: "場所",
      locationPlaceholder: "会議室1",
      description: "説明",
      descriptionPlaceholder: "説明を追加...",
      inviteMembers: "メンバーを招待",
      invitePlaceholder: "ユーザーまたはグループを追加...",
      sendInvite: "招待を送信",
      create: "作成"
    },
    role: '役割',
  },
};

// SVG Icons (to avoid external dependencies)
const HomeIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-home"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>);
const UserIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-user"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>);
const MessageSquareIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-message-square"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>);
const StarIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-star"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>);
const MapIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-map"><polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21"/><line x1="9" x2="9" y1="3" y2="18"/><line x1="15" x2="15" y1="6" y2="21"/></svg>);
const DollarSignIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-dollar-sign"><line x1="12" x2="12" y1="2" y2="22"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>);
const LogOutIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-log-out"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" x2="9" y1="12" y2="12"/></svg>);
const SettingsIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-settings"><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 0 2.73l-.15.1a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1-1-1.73l-.43-.25a2 2 0 0 1-2 0l-.15.08a2 2 0 0 0-2.73-.73l-.22-.39a2 2 0 0 0 .73-2.73l.15-.09a2 2 0 0 1 0-2.73l-.15-.1a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/></svg>);

// This is the main application component. It manages global state like
// authentication, user data, language, and routing.
function App() {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [language, setLanguage] = useState('en'); // Default to English
  const navigate = useNavigate();

  // Dynamically derive translations based on the current language
  const T = translations[language];

  const value = {
    user,
    setUser,
    role,
    setRole,
    language,
    setLanguage,
    T,
    handleLogout: () => {
      setUser(null);
      setRole(null);
      localStorage.removeItem('authToken');
      navigate('/');
    },
    handleLogin: (userData, token) => {
      setUser(userData);
      setRole(userData.role);
      localStorage.setItem('authToken', token);
      navigate('/dashboard');
    },
  };

  return (
    <AppContext.Provider value={value}>
      <div className="min-h-screen bg-gray-50 font-sans antialiased text-gray-800 flex flex-col">
        <Header />
        <main className="flex-grow container mx-auto px-4 py-8">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Auth />} />
            <Route path="/signup" element={<Auth isSignup />} />
            <Route path="/dashboard" element={user ? <Dashboard /> : <LoginPrompt />} />
            <Route path="/profile" element={user ? <Profile /> : <LoginPrompt />} />
            <Route path="/matches" element={user ? <Matches /> : <LoginPrompt />} />
            <Route path="/donations" element={user ? <Donations /> : <LoginPrompt />} />
            <Route path="/admin" element={role === 'admin' ? <AdminPanel /> : <LoginPrompt />} />
            <Route path="/ratings/:id" element={user ? <Ratings /> : <LoginPrompt />} />
            <Route path="/event-map/*" element={<EventMapContainer />} />
            <Route path="/event-scheduler" element={<ErrorBoundary><EventScheduler /></ErrorBoundary>} />
          </Routes>
        </main>
        <Footer />
      </div>
    </AppContext.Provider>
  );
}

// Header Component
function Header() {
  const { user, role, language, setLanguage, T, handleLogout } = useContext(AppContext);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDiagnosticsOpen, setIsDiagnosticsOpen] = useState(false);

  const navLinks = user ? (
    <>
      <NavLink to="/dashboard">{T.dashboard}</NavLink>
      <NavLink to="/matches">{T.matches}</NavLink>
      <NavLink to="/profile">{T.profile}</NavLink>
      <NavLink to="/donations">{T.donations}</NavLink>
      {role === 'admin' && <NavLink to="/admin">{T.admin}</NavLink>}
    </>
  ) : (
    <>
      <NavLink to="/login">{T.login}</NavLink>
      <NavLink to="/signup">{T.signup}</NavLink>
    </>
  );
  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <div className="container mx-auto px-4 py-4 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to="/" className="text-xl font-bold text-emerald-600 flex items-center">
            <img src="/hopelink.svg" className="logo h-10 w-10 md:h-12 md:w-12" alt="Hopelink logo" />
          </Link>
          <nav className="hidden md:flex space-x-2">
            <NavLink to="/">{T.home}</NavLink>
            {navLinks}
          </nav>
        </div>
        <div className="flex items-center space-x-4">
          {/* Diagnostics Button - Only show when enabled */}
          {diagnosticsConfig.enabled && (
            <button
              onClick={() => setIsDiagnosticsOpen(true)}
              className="hidden md:flex items-center px-3 py-2 bg-yellow-500 hover:bg-yellow-600 text-white text-sm rounded-lg transition duration-200"
              title="Firebase Emulator Diagnostics"
            >
              🧪 Diagnostics
            </button>
          )}
          
          {user && (
            <button
              onClick={handleLogout}
              className="hidden md:flex items-center px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition duration-200 shadow-md"
            >
              <LogOutIcon className="w-4 h-4 mr-2" /> {T.logout}
            </button>
          )}
          <select
            value={language} // Bind the dropdown to the current language state
            onChange={(e) => setLanguage(e.target.value)} // Update the language state
            className="p-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            <option value="en">English</option>
            <option value="ja">日本語</option>
          </select>
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" x2="20" y1="12" y2="12"/><line x1="4" x2="20" y1="6" y2="6"/><line x1="4" x2="20" y1="18" y2="18"/></svg>
          </button>
        </div>
      </div>
      {isMobileMenuOpen && (
        <nav className="md:hidden flex flex-col items-start px-4 py-2 border-t border-gray-200 space-y-1">
          <NavLink to="/" onClick={() => setIsMobileMenuOpen(false)}>{T.home}</NavLink>
          {navLinks}
          
          {/* Mobile Diagnostics Button */}
          {diagnosticsConfig.enabled && (
            <button
              onClick={() => { setIsDiagnosticsOpen(true); setIsMobileMenuOpen(false); }}
              className="w-full text-left px-3 py-2 text-yellow-600 hover:bg-gray-100 rounded-lg transition duration-200"
            >
              🧪 Diagnostics
            </button>
          )}
          
          {user && (
            <button
              onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }}
              className="w-full text-left px-3 py-2 text-red-500 hover:bg-gray-100 rounded-lg transition duration-200"
            >
              <LogOutIcon className="w-4 h-4 inline-block mr-2" /> {T.logout}
            </button>
          )}
        </nav>
      )}

      {/* Diagnostics Panel */}
      <DiagnosticsPanel 
        isOpen={isDiagnosticsOpen} 
        onClose={() => setIsDiagnosticsOpen(false)} 
      />
    </header>
  );
}
function NavLink({ to, children, ...props }) {
  return (
    <Link
      to={to}
      className="px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition duration-200 flex items-center"
      {...props}
    >
      {children}
    </Link>
  );
}
// Footer Component
function Footer() {
  const { T } = useContext(AppContext);
  return (
    <footer className="bg-gray-800 text-white p-4 text-center mt-8">
      <p>&copy; 2025 {T.appName}. All rights reserved.</p>
    </footer>
  );
}
// Home Page
function Home() {
  const { T, language } = useContext(AppContext); // Use the language state directly
  return (
    <div className="text-center p-8 bg-white rounded-lg shadow-lg">
      <h1 className="text-4xl font-bold text-emerald-600 mb-4">{T.appName}</h1>
      <p className="text-lg text-gray-600 mb-8">{T.welcome}</p>
      <div className="flex flex-col md:flex-row justify-center space-y-4 md:space-y-0 md:space-x-4">
        <Link to="/signup?role=patient" className="w-full md:w-auto px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-lg shadow-md transition duration-200">
          {T.findHelp}
        </Link>
        <Link to="/signup?role=volunteer" className="w-full md:w-auto px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold rounded-lg shadow-md transition duration-200">
          {T.offerHelp}
        </Link>
      </div>
      <div className="mt-12">
        <h2 className="text-2xl font-semibold mb-4">
          {language === 'en' ? 'Our Mission' : '私たちの使命'} {/* Use language state */}
        </h2>
        <p className="text-gray-700 max-w-2xl mx-auto">
          {T.mission} {/* Dynamically display the mission text based on the selected language */}
        </p>
      </div>
    </div>
  );
}
// Authentication Component (Login & Signup)
function Auth({ isSignup = false }) {
  const { handleLogin, T, language } = useContext(AppContext);
  const { signUp, signIn, loading, error } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState('patient');
  const [authError, setAuthError] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (!isSignup) return;
    const roleParam = (searchParams.get('role') || '').toLowerCase();
    if (roleParam === 'volunteer') {
      setRole('volunteer');
    } else if (roleParam === 'patient') {
      setRole('patient');
    }
  }, [isSignup, searchParams]);

  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthError('');

    try {
      if (isSignup) {
        console.log('🚀 Attempting Firebase signup:', { email, role, firstName, lastName });
        
        const credentials = {
          email,
          password,
          firstName,
          lastName,
          phone,
          role
        };
        
        await signUp(credentials);
        console.log('✅ Firebase signup successful');
        
        // Create user data for app context
        const userData = { 
          id: Date.now(), 
          email, 
          role, 
          name: `${firstName} ${lastName}`.trim() || 'User',
          firstName,
          lastName,
          phone
        };
        
        handleLogin(userData, 'firebase-token');
      } else {
        console.log('🔐 Attempting Firebase login:', { email });
        
        const credentials = { email, password };
        await signIn(credentials);
        console.log('✅ Firebase login successful');
        
        // Create user data for app context
        const userData = { 
          id: 1, 
          email, 
          role: email === 'admin@example.com' ? 'admin' : 'patient', 
          name: 'User',
          location: [35.68, 139.75] 
        };
        
        handleLogin(userData, 'firebase-token');
      }
    } catch (error) {
      console.error('❌ Firebase authentication failed:', error);
      setAuthError(error.message || 'Authentication failed. Please try again.');
    }
  };

  return (
    <div className="max-w-md mx-auto p-8 bg-white rounded-xl shadow-lg">
      <h2 className="text-3xl font-bold text-center text-emerald-600 mb-6">
        {isSignup ? T.signup : T.login}
      </h2>
      
      {/* Show Firebase Auth errors */}
      {(error || authError) && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
          <p className="text-sm">{error || authError}</p>
        </div>
      )}
      
      <form onSubmit={handleAuth} className="space-y-4">
        <div>
          <label className="block text-gray-600">{T.email}</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full p-3 mt-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            required
            disabled={loading}
          />
        </div>
        <div>
          <label className="block text-gray-600">{T.password}</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full p-3 mt-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            required
            disabled={loading}
            minLength="6"
          />
        </div>
        
        {isSignup && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-gray-600">First Name</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full p-3 mt-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  disabled={loading}
                />
              </div>
              <div>
                <label className="block text-gray-600">Last Name</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full p-3 mt-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  disabled={loading}
                />
              </div>
            </div>
            
            <div>
              <label className="block text-gray-600">Phone (Optional)</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full p-3 mt-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                disabled={loading}
              />
            </div>
            
            <div>
              <label className="block text-gray-600">{T.role}</label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="w-full p-3 mt-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                disabled={loading}
              >
                <option value="patient">{T.patient}</option>
                <option value="volunteer">{T.volunteer}</option>
              </select>
            </div>
          </>
        )}
        
        <button
          type="submit"
          disabled={loading}
          className={`w-full px-4 py-3 font-bold rounded-lg shadow-md transition duration-200 ${
            loading
              ? 'bg-gray-400 cursor-not-allowed text-gray-700'
              : 'bg-emerald-500 hover:bg-emerald-600 text-white'
          }`}
        >
          {loading 
            ? (isSignup ? 'Creating Account...' : 'Signing In...') 
            : (isSignup ? T.signup : T.login)
          }
        </button>
      </form>
      
      <p className="mt-4 text-center text-sm text-gray-500">
        {isSignup ? T.alreadyHaveAccount : T.dontHaveAccount}{' '}
        <Link to={isSignup ? '/login' : '/signup'} className="text-emerald-500 hover:underline ml-1">
          {isSignup ? T.login : T.signup}
        </Link>
      </p>
      <div className="mt-4 text-center">
        <Link to="/event-map" className="text-emerald-500 hover:underline">
          {language === 'en' ? 'Skip to Access' : 'アクセスにスキップ'}
        </Link>
      </div>
    </div>
  );
}
// Login Prompt
function LoginPrompt() {
  const { T } = useContext(AppContext);
  return (
    <div className="text-center p-8 bg-white rounded-lg shadow-lg">
      <p className="text-xl text-gray-600">{T.loginPrompt}</p>
      <Link to="/login" className="mt-4 inline-block px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-lg shadow-md transition duration-200">
        {T.login}
      </Link>
    </div>
  );
}
// Dashboard Component
function Dashboard() {
  const { user, role, T } = useContext(AppContext);
  if (!user) {
    return <LoginPrompt />;
  }
  // Mock data for needs and offers
  const allNeeds = [
    { id: 1, patient: 'Jane Doe', location: [35.65, 139.8], needs: 'Cooking, Grocery Shopping', schedule: 'Mon, Wed' },
    { id: 2, patient: 'Richard Roe', location: [35.7, 139.7], needs: 'House Cleaning', schedule: 'Fri' },
  ];
  const allOffers = [
    { id: 1, volunteer: 'Alice Smith', location: [35.68, 139.75], offers: 'Cooking, Cleaning', schedule: 'Tue, Thu' },
    { id: 2, volunteer: 'Bob Johnson', location: [35.67, 139.73], offers: 'Grocery Shopping', schedule: 'Sat' },
  ];
  return (
    <div className="space-y-8">
      <h2 className="text-3xl font-bold text-emerald-600 mb-4">{T.dashboard}</h2>
      {role === 'patient' && (
        <div className="bg-white p-6 rounded-xl shadow-lg">
          <h3 className="text-2xl font-semibold text-gray-700 mb-4">{T.volunteerOffers}</h3>
          <div className="space-y-4">
            {allOffers.length > 0 ? (
              allOffers.map((offer) => (
                <div key={offer.id} className="p-4 bg-gray-100 rounded-lg flex flex-col md:flex-row justify-between items-start md:items-center">
                  <div>
                    <h4 className="font-bold">{offer.volunteer}</h4>
                    <p className="text-gray-600 text-sm">{T.offers}: {offer.offers}</p>
                    <p className="text-gray-600 text-sm">{T.schedule}: {offer.schedule}</p>
                  </div>
                  <Link to={`/profile`} className="mt-2 md:mt-0 px-4 py-2 bg-emerald-500 text-white rounded-lg text-sm hover:bg-emerald-600 transition duration-200">
                    {T.viewProfile}
                  </Link>
                </div>
              ))
            ) : (
              <p className="text-gray-500">{T.noUsersFound}</p>
            )}
          </div>
        </div>
      )}
      {role === 'volunteer' && (
        <div className="bg-white p-6 rounded-xl shadow-lg">
          <h3 className="text-2xl font-semibold text-gray-700 mb-4">{T.patientNeeds}</h3>
          <div className="space-y-4">
            {allNeeds.length > 0 ? (
              allNeeds.map((need) => (
                <div key={need.id} className="p-4 bg-gray-100 rounded-lg flex flex-col md:flex-row justify-between items-start md:items-center">
                  <div>
                    <h4 className="font-bold">{need.patient}</h4>
                    <p className="text-gray-600 text-sm">{T.needs}: {need.needs}</p>
                    <p className="text-gray-600 text-sm">{T.schedule}: {need.schedule}</p>
                  </div>
                  <Link to={`/profile`} className="mt-2 md:mt-0 px-4 py-2 bg-emerald-500 text-white rounded-lg text-sm hover:bg-emerald-600 transition duration-200">
                    {T.viewProfile}
                  </Link>
                </div>
              ))
            ) : (
              <p className="text-gray-500">{T.noUsersFound}</p>
            )}
          </div>
        </div>
      )}
      {role === 'admin' && <AdminPanel />}
    </div>
  );
}
// Profile Component
function Profile() {
  const { user, role, T, setUser } = useContext(AppContext);
  const [profileData, setProfileData] = useState(user);
  const [statusMessage, setStatusMessage] = useState('');
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({ ...prev, [name]: value }));
  };
  const handleSave = async (e) => {
    e.preventDefault();
    try {
      // Mock API call to save profile
      // await fetch('/api/profile', { method: 'POST', body: JSON.stringify(profileData) });
      setUser(profileData);
      localStorage.setItem('user', JSON.stringify(profileData));
      setStatusMessage(T.profileSaveSuccess);
    } catch (error) {
      console.error('Failed to save profile:', error);
      setStatusMessage(T.profileSaveError);
    }
  };
  if (!user) return <LoginPrompt />;
  return (
    <div className="max-w-2xl mx-auto p-8 bg-white rounded-xl shadow-lg">
      <h2 className="text-3xl font-bold text-center text-emerald-600 mb-6">{T.profile}</h2>
      {statusMessage && (
        <div className="p-4 mb-4 text-sm text-green-700 bg-green-100 rounded-lg text-center">
          {statusMessage}
        </div>
      )}
      <form onSubmit={handleSave} className="space-y-4">
        <div>
          <label className="block text-gray-600 font-medium">Email</label>
          <input
            type="email"
            value={user.email}
            readOnly
            className="w-full p-3 mt-1 bg-gray-50 border border-gray-300 rounded-lg"
          />
        </div>
        <div>
          <label className="block text-gray-600 font-medium">{T.aboutMe}</label>
          <textarea
            name="bio"
            value={profileData?.bio || ''}
            onChange={handleInputChange}
            className="w-full p-3 mt-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            rows="4"
          />
        </div>
        {role === 'patient' && (
          <div>
            <label className="block text-gray-600 font-medium">{T.myNeeds}</label>
            <textarea
              name="needs"
              value={profileData?.needs || ''}
              onChange={handleInputChange}
              className="w-full p-3 mt-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              placeholder="e.g. Cooking, cleaning, shopping..."
              rows="3"
            />
          </div>
        )}
        {role === 'volunteer' && (
          <div>
            <label className="block text-gray-600 font-medium">{T.myOffers}</label>
            <textarea
              name="offers"
              value={profileData?.offers || ''}
              onChange={handleInputChange}
              className="w-full p-3 mt-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              placeholder="e.g. Cooking, cleaning, shopping..."
              rows="3"
            />
          </div>
        )}
        <div>
          <label className="block text-gray-600 font-medium mt-4">{T.paypay} (JP) / {T.venmo} (US)</label>
          <input
            type="text"
            name="paymentId"
            value={profileData?.paymentId || ''}
            onChange={handleInputChange}
            className="w-full p-3 mt-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            placeholder="e.g. @username for Venmo or phone number for PayPay"
          />
        </div>
        <button
          type="submit"
          className="w-full px-4 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-lg shadow-md transition duration-200"
        >
          {T.saveProfile}
        </button>
      </form>
    </div>
  );
}
// Matches Component
function Matches() {
  const { user, T } = useContext(AppContext);
  if (!user) return <LoginPrompt />;
  // Mock match data
  const matches = [
    {
      id: 1,
      name: 'Alice Smith',
      role: 'volunteer',
      location: [35.68, 139.75],
      schedule: 'Tue, Thu',
      needs: 'Cooking, Cleaning',
    },
    {
      id: 2,
      name: 'Bob Johnson',
      role: 'patient',
      location: [35.67, 139.73],
      schedule: 'Sat',
      needs: 'Grocery Shopping',
    },
  ];
  return (
    <div className="space-y-8">
      <h2 className="text-3xl font-bold text-center text-emerald-600 mb-6">{T.matches}</h2>
      <div className="bg-white p-6 rounded-xl shadow-lg">
        <h3 className="text-2xl font-semibold text-gray-700 mb-4">{T.locationMatch}</h3>
        <MapContainer center={[35.68, 139.75]} zoom={13} className="h-96 w-full rounded-lg shadow-inner">
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
          />
          {matches.map((match) => (
            <Marker key={match.id} position={match.location}>
              <Popup>
                <strong>{match.name} ({match.role})</strong><br />
                {T.schedule}: {match.schedule}
              </Popup>
            </Marker>
          ))}
          {user.location && (
            <Marker position={user.location} >
              <Popup>
                <strong>{user.name} ({T.profile})</strong>
              </Popup>
            </Marker>
          )}
        </MapContainer>
      </div>
      <div className="bg-white p-6 rounded-xl shadow-lg">
        <h3 className="text-2xl font-semibold text-gray-700 mb-4">{T.timeMatch}</h3>
        <ul className="space-y-4">
          {matches.length > 0 ? (
            matches.map((match) => (
              <li key={match.id} className="p-4 bg-gray-100 rounded-lg flex justify-between items-center">
                <div>
                  <h4 className="font-bold">{match.name} ({T[match.role]})</h4>
                  <p className="text-gray-600 text-sm">{T.schedule}: {match.schedule}</p>
                </div>
                <div className="space-x-2">
                  <button className="px-4 py-2 bg-emerald-500 text-white rounded-lg text-sm hover:bg-emerald-600 transition duration-200">
                    {T.tip}
                  </button>
                  <Link to={`/ratings/${match.id}`} className="px-4 py-2 bg-gray-300 text-gray-800 rounded-lg text-sm hover:bg-gray-400 transition duration-200">
                    {T.rating}
                  </Link>
                </div>
              </li>
            ))
          ) : (
            <p className="text-gray-500">{T.noMatches}</p>
          )}
        </ul>
      </div>
    </div>
  );
}
// Donations Component
function Donations() {
  const { T } = useContext(AppContext);
  const [amount, setAmount] = useState(1000);
  const [statusMessage, setStatusMessage] = useState('');
  const handleDonate = async () => {
    try {
      // Mock API call for donation
      // await fetch('/api/donate', { method: 'POST', body: JSON.stringify({ amount }) });
      setStatusMessage('Donation successful! Thank you.');
    } catch (error) {
      setStatusMessage('Donation failed. Please try again.');
    }
  };
  return (
    <div className="max-w-lg mx-auto p-8 bg-white rounded-xl shadow-lg text-center">
      <h2 className="text-3xl font-bold text-emerald-600 mb-4">{T.donations}</h2>
      <p className="text-gray-600 mb-6">{T.donationMessage}</p>
      <div className="space-y-4">
        <div className="flex justify-center items-center space-x-2">
          <DollarSignIcon className="w-8 h-8 text-emerald-500" />
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-32 p-3 text-center border border-gray-300 rounded-lg text-xl font-bold focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
          <span className="text-xl">JPY</span>
        </div>
        {statusMessage && <p className="text-green-600">{statusMessage}</p>}
        <button
          onClick={handleDonate}
          className="w-full px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-lg shadow-md transition duration-200"
        >
          {T.donate}
        </button>
      </div>
    </div>
  );
}
// Admin Panel Component
function AdminPanel() {
  const { T } = useContext(AppContext);
  const [adsEnabled, setAdsEnabled] = useState(true);
  const [revenue, setRevenue] = useState(124500); // Mock revenue in JPY
  const [showModal, setShowModal] = useState(false);
  const toggleAds = async () => {
    try {
      // Mock API call to toggle ads
      // await fetch('/api/admin/toggle-ads', { method: 'POST', body: JSON.stringify({ adsEnabled: !adsEnabled }) });
      setAdsEnabled(!adsEnabled);
      // Use a custom message box instead of alert()
      console.log('Ad status updated successfully.');
    } catch (error) {
      console.error('Failed to update ad status.');
    }
  };
  return (
    <div className="max-w-2xl mx-auto p-8 bg-white rounded-xl shadow-lg">
      <h2 className="text-3xl font-bold text-center text-emerald-600 mb-6">{T.adminControl}</h2>
      <div className="space-y-4">
        <div className="flex items-center justify-between p-4 bg-gray-100 rounded-lg">
          <span className="font-semibold text-gray-700">{T.adStatus}:</span>
          <span className={`px-3 py-1 rounded-full text-sm font-bold ${adsEnabled ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
            {adsEnabled ? 'On' : 'Off'}
          </span>
        </div>
        <div className="flex items-center justify-between p-4 bg-gray-100 rounded-lg">
          <span className="font-semibold text-gray-700">{T.adRevenue}:</span>
          <span className="text-xl font-bold text-emerald-600">¥{revenue.toLocaleString()}</span>
        </div>
        <button
          onClick={toggleAds}
          className={`w-full px-4 py-3 font-bold rounded-lg shadow-md transition duration-200 ${adsEnabled ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-green-500 hover:bg-green-600 text-white'}`}
        >
          {T.toggleAds}
        </button>
        <button
          onClick={() => setShowModal(true)}
          className="w-full px-4 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold rounded-lg shadow-md transition duration-200"
        >
          Manage Ad Types
        </button>
      </div>
      {showModal && <AdManagementModal onClose={() => setShowModal(false)} />}
    </div>
  );
}
function AdManagementModal({ onClose }) {
  // Simple modal UI for ad type management
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full">
        <h3 className="text-2xl font-bold mb-4">Manage Ad Types</h3>
        <p>This is a placeholder for a more complex ad management UI. Here, you could integrate with an ad network API to control categories, frequency, and placement.</p>
        <button onClick={onClose} className="mt-4 px-4 py-2 bg-gray-300 hover:bg-gray-400 rounded-lg">
          Close
        </button>
      </div>
    </div>
  );
}
// Added a new Ratings component to handle anonymous ratings
function Ratings() {
  const { T } = useContext(AppContext);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [demeanor, setDemeanor] = useState('average');
  const [friendliness, setFriendliness] = useState('average');
  const navigate = useNavigate();
  const handleRatingSubmit = (e) => {
    e.preventDefault();
    // This would be a mock API call to save the anonymous rating
    console.log('Rating submitted:', { rating, comment, demeanor, friendliness });
    console.log('Thank you for your feedback!'); // Use console.log or a modal instead of alert()
    navigate('/dashboard');
  };
  return (
    <div className="max-w-xl mx-auto p-8 bg-white rounded-xl shadow-lg">
      <h2 className="text-3xl font-bold text-center text-emerald-600 mb-6">{T.anonRating}</h2>
      <form onSubmit={handleRatingSubmit} className="space-y-6">
        <div>
          <label className="block text-gray-600 font-medium mb-2">{T.rating}</label>
          <div className="flex justify-center space-x-2">
            {[1, 2, 3, 4, 5].map(star => (
              <button
                type="button"
                key={star}
                onClick={() => setRating(star)}
                className={`text-4xl transition-colors duration-200 ${star <= rating ? 'text-yellow-400' : 'text-gray-300'}`}
              >
                <StarIcon className="w-10 h-10" fill="currentColor" />
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-gray-600 font-medium mb-2">{T.demenaor}</label>
          <div className="flex justify-between space-x-2">
            {['excellent', 'good', 'average', 'poor'].map(option => (
              <label key={option} className="flex-1 text-center p-3 border rounded-lg cursor-pointer">
                <input
                  type="radio"
                  name="demeanor"
                  value={option}
                  checked={demeanor === option}
                  onChange={(e) => setDemeanor(e.target.value)}
                  className="hidden"
                />
                <span className={`block transition-colors duration-200 ${demeanor === option ? 'bg-emerald-500 text-white rounded-lg' : 'text-gray-700 hover:bg-gray-100 rounded-lg'}`}>{T[option]}</span>
              </label>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-gray-600 font-medium mb-2">{T.friendly}</label>
          <div className="flex justify-between space-x-2">
            {['excellent', 'good', 'average', 'poor'].map(option => (
              <label key={option} className="flex-1 text-center p-3 border rounded-lg cursor-pointer">
                <input
                  type="radio"
                  name="friendliness"
                  value={option}
                  checked={friendliness === option}
                  onChange={(e) => setFriendliness(e.target.value)}
                  className="hidden"
                />
                <span className={`block transition-colors duration-200 ${friendliness === option ? 'bg-emerald-500 text-white rounded-lg' : 'text-gray-700 hover:bg-gray-100 rounded-lg'}`}>{T[option]}</span>
              </label>
            ))}
          </div>
        </div>
        <button
          type="submit"
          className="w-full px-4 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-lg shadow-md transition duration-200"
        >
          {T.submit}
        </button>
      </form>
    </div>
  );
}
function EventMapContainer() {
  return (
    <Routes>
      <Route index element={<EventMap />} />
      <Route path="scheduler" element={<ErrorBoundary><EventScheduler /></ErrorBoundary>} />
    </Routes>
  );
}
function EventMap() {
  const navigate = useNavigate();
  const { T, language } = useContext(AppContext);

  // Add state to track the dropped pin and map center
  const [droppedPin, setDroppedPin] = useState(null);
  const [mapCenter, setMapCenter] = useState([35.68, 139.75]); // Default to Tokyo
  const [locationError, setLocationError] = useState(null);

  // Function to handle GPS errors with specific messages
  const handleLocationError = (error) => {
    let errorMessage;
    switch(error.code) {
      case error.PERMISSION_DENIED:
        errorMessage = language === 'en' 
          ? "Location access denied. Please enable location permissions in your browser settings."
          : "位置情報アクセスが拒否されました。ブラウザ設定で位置情報の許可を有効にしてください。";
        break;
      case error.POSITION_UNAVAILABLE:
        errorMessage = language === 'en'
          ? "Location information is unavailable. Please check your device's GPS settings."
          : "位置情報が利用できません。デバイスのGPS設定を確認してください。";
        break;
      case error.TIMEOUT:
        errorMessage = language === 'en'
          ? "Location request timed out. Please try again or click on the map to set your location."
          : "位置情報の取得がタイムアウトしました。再試行するか、マップをクリックして位置を設定してください。";
        break;
      default:
        errorMessage = language === 'en'
          ? "An unknown error occurred while getting your location. You can click on the map to set your location manually."
          : "位置情報の取得中に不明なエラーが発生しました。マップをクリックして手動で位置を設定できます。";
        break;
    }
    setLocationError(errorMessage);
    console.log('GPS location error:', errorMessage);
  };

  // NEW: attempt to drop a pin at the device's GPS location by default
  useEffect(() => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude, longitude } = pos.coords;
          const location = { lat: latitude, lng: longitude };
          setDroppedPin(location);
          setMapCenter([latitude, longitude]); // Center map on user location
          setLocationError(null); // Clear any previous errors
        },
        handleLocationError,
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
      );
    } else {
      setLocationError(language === 'en' 
        ? "Geolocation is not supported by this browser. Please click on the map to set your location."
        : "このブラウザは位置情報をサポートしていません。マップをクリックして位置を設定してください。"
      );
    }
  }, [language]);

  // Handle map clicks to drop a pin
  function MapClickHandler() {
    useMapEvents({
      click(e) {
        setDroppedPin(e.latlng);
      },
    });
    return null;
  }

  // Component to recenter map when GPS location is found
  function MapRecenter({ center }) {
    const map = useMap();
    useEffect(() => {
      map.setView(center, 15); // Higher zoom when centering on user location
    }, [center, map]);
    return null;
  }

  const handleScheduleEvent = () => {
    navigate('scheduler');
  };

  return (
    <div className="h-full">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-bold text-emerald-600">Event Map</h2>
        <button
          onClick={handleScheduleEvent}
          className="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-lg shadow-md transition duration-200"
        >
          {language === 'en' ? 'Schedule Request' : '依頼をスケジュール'}
        </button>
      </div>
      
      {/* Display location error message if any */}
      {locationError && (
        <div className="mb-4 p-4 bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700">
          <p className="text-sm">{locationError}</p>
        </div>
      )}

      <MapContainer center={mapCenter} zoom={13} className="h-96 w-full rounded-lg shadow-inner">
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
        />
        {/* Recenter map when GPS location is available */}
        {droppedPin && <MapRecenter center={[droppedPin.lat, droppedPin.lng]} />}
        {/* Listen for clicks to set the dropped pin */}
        <MapClickHandler />
        {/* Render the dropped pin as a draggable marker */}
        {droppedPin && (
          <Marker
            position={droppedPin}
            draggable
            icon={defaultMarkerIcon}
            eventHandlers={{
              dragend: (e) => {
                const pos = e.target.getLatLng();
                setDroppedPin(pos);
              },
            }}
          >
            <Popup>{language === 'en' ? 'GPS Location' : 'GPS位置'}</Popup>
          </Marker>
        )}
      </MapContainer>
    </div>
  );
}

export default App;
export { AppContext };
